/**
 * Project home — one project's stats, its stalled list, and the walks it came
 * from.
 *
 * Layout follows the design's PROJECT HOME block: a header with the project
 * crumb and the two calls to action, a four-tile stat row, then a two-column
 * body — "Needs you first" on the left, Recent walks on the right.
 *
 * ## Ball-in-court is gone (spec §4)
 *
 * The design puts a ball pill on every stalled row and a distribution chart
 * above the walks. Both are cut, console-wide: `ballHolder` is free-text and
 * reads `Unassigned` on essentially every real row here, so the chart ranked
 * one bar called `Unassigned` and the pills repeated it. `getProjectHome`
 * still RETURNS `ballDistribution` — it is `getBrief`'s own derivation and
 * ripping a shared query apart to delete one panel would be the larger
 * mistake — so this is a rendering decision, and
 * `tests/guards/noBallAffordance.test.ts` holds it.
 *
 * Every number on this page comes from `getProjectHome`, which is
 * `getBrief`'s own derivations narrowed to one site. Nothing here re-derives
 * "stalled", "open" or "not reviewed" from the rows it was handed: two panels
 * that computed the same word separately are two panels that will eventually
 * disagree, and this console exists to be trusted about exactly that word.
 *
 * ## The walk rows carry no duration (spec §7.3)
 *
 * The design writes `AUG 5 · 24 MIN`. The length of a walk is the length of
 * its audio, audio is never user-facing, and the server does not send one —
 * see `getWalkDetail.ts`'s header. These rows print the date and what the
 * walk produced, which is what a reader actually needs from a history.
 */

import { useState } from "react";
import {
  fetchProjectHome,
  type ApiError,
  type ConsoleRow,
  type ProjectHomeResponse,
  type ProjectWalkResponse,
} from "../api";
import { useResource } from "../useResource";
import { PhotoFullscreenViewer, type FullscreenPhoto } from "../components/PhotoFullscreenViewer";
import { C, FONT_MONO, projectDot } from "../tokens";
import {
  Card,
  ConsoleViewProps,
  EmptyPanel,
  Eyebrow,
  EvidenceTile,
  FailurePanel,
  KindChip,
  LoadingPanel,
  META,
  Pill,
  ROW_LINE,
  ROW_TITLE,
  SEVERITY,
  StaleBanner,
  ViewBody,
  ViewHeader,
  ageOf,
  dayLabel,
  metaLine,
  plural,
} from "../components/chrome";

/** One of the four numbers across the top. `ink` carries the alarm: the
 *  design turns Stalled red when it is non-zero and Not reviewed amber, and
 *  leaves the rest in ink. */
function Stat({
  label,
  value,
  sub,
  ink,
  testId,
}: {
  label: string;
  value: string;
  sub: string;
  ink: string;
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{
        // A cell of the band, not a card of its own: the redesign puts the
        // four numbers on ONE surface divided by rules, because they are one
        // reading. Four separate cards made the reader's eye stop four times
        // to learn one thing.
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "0 22px",
        borderLeft: `1px solid ${C.hairRow}`,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: 26,
          fontWeight: 700,
          letterSpacing: "-0.65px",
          lineHeight: 1,
          color: ink,
        }}
      >
        {value}
      </span>
      <span style={{ ...META, fontSize: 10.93, color: C.sub2 }}>{label}</span>
      <span style={{ fontSize: 13.23, color: C.sub2 }}>{sub}</span>
    </div>
  );
}

/** A stalled row, in the design's Project-home anatomy: age first, then the
 *  kind chip and title over a mono meta line, then who is holding it. */
function StalledRow({
  row,
  siteName,
  href,
  onOpen,
  first,
}: {
  row: ConsoleRow;
  siteName: string | undefined;
  href: string;
  onOpen: () => void;
  first: boolean;
}) {
  const age = ageOf(row);
  const severity = row.severity ? SEVERITY[row.severity] : undefined;

  return (
    <a
      href={href}
      data-testid="project-stalled-row"
      data-item-id={row.id}
      onClick={(event) => {
        event.preventDefault();
        onOpen();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 13,
        padding: "12px 4px",
        borderTop: first ? "1px solid transparent" : `1px solid ${C.line}`,
      }}
    >
      <span
        style={{
          width: 34,
          flexShrink: 0,
          fontFamily: FONT_MONO,
          fontSize: 14.37,
          fontWeight: 700,
          color: age.ink,
        }}
      >
        {age.text}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...ROW_LINE, gap: 8 }}>
          <KindChip kind={row.kind} />
          <span
            data-testid="row-title"
            style={{ ...ROW_TITLE, fontSize: 15.52, fontWeight: 600, letterSpacing: "-0.2px" }}
          >
            {row.title}
          </span>
          {severity && <Pill {...severity} />}
        </span>
        <span
          style={{
            ...META,
            display: "block",
            marginTop: 5,
            fontSize: 10.93,
            fontWeight: 400,
            letterSpacing: "0.3px",
            color: C.sub2,
          }}
        >
          {metaLine(row, siteName)}
        </span>
      </span>
    </a>
  );
}

/** One walk. Date, who walked it, and what it produced — no duration. */
function WalkRow({
  walk,
  href,
  onOpen,
  first,
}: {
  walk: ProjectWalkResponse;
  href: string;
  onOpen: () => void;
  first: boolean;
}) {
  return (
    <a
      href={href}
      data-testid="project-walk-row"
      data-visit-id={walk.id}
      onClick={(event) => {
        event.preventDefault();
        onOpen();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 4px",
        borderTop: first ? "1px solid transparent" : `1px solid ${C.line}`,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: 14.95,
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {walk.name || "Untitled walk"}
        </span>
        <span
          style={{
            ...META,
            display: "block",
            marginTop: 3,
            fontSize: 10.35,
            fontWeight: 400,
            letterSpacing: "0.4px",
            color: C.sub2,
          }}
        >
          {`${dayLabel(walk.startedAt)} · ${walk.creatorName || walk.creator || "Unknown"}`}
        </span>
      </span>
      <span
        style={{ fontFamily: FONT_MONO, fontSize: 12.08, color: C.sub2, flexShrink: 0 }}
        data-testid="project-walk-items"
      >
        {plural(walk.itemCount, "item", "items")}
      </span>
    </a>
  );
}

export function ProjectHome(props: ConsoleViewProps & { jobSiteId: string }) {
  const {
    jobSiteId,
    search,
    refreshKey,
    personalHref,
    onGoPersonal,
    hrefForItem,
    onOpenItem,
    hrefForWalk,
    onOpenWalk,
    hrefForReview,
    onOpenReview,
    hrefWithParam,
    onNavigate,
  } = props;
  const [evidenceViewer, setEvidenceViewer] = useState<number | null>(null);

  const { data, failure, refreshFailure } = useResource<ProjectHomeResponse>(
    `project:${jobSiteId}${search}`,
    () => fetchProjectHome(jobSiteId, search),
    refreshKey,
  );

  if (failure) {
    return (
      <FailurePanel
        failure={failure as ApiError}
        personalHref={personalHref}
        onGoPersonal={onGoPersonal}
      />
    );
  }
  if (!data) return <LoadingPanel shape="project" />;

  const name = data.siteName || data.jobSiteId;
  const { stats } = data;
  const latestWalk = data.walks[0];
  const recentPhotos: FullscreenPhoto[] = data.recentEvidence.slice(0, 6).map((evidence) => ({
    id: evidence.itemId,
    url: evidence.thumbnailUrl,
    thumbnailUrl: evidence.thumbnailUrl,
    description: evidence.title,
    capturedAt: evidence.capturedAt,
    siteName: name,
  }));

  return (
    <>
      <ViewHeader
        crumb={
          stats.lastWalkAt
            ? `Project · last walked ${dayLabel(stats.lastWalkAt)}`
            : "Project · never walked"
        }
        title={name}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            flexWrap: "wrap",
            margin: "14px 0 0",
          }}
        >
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: 5,
              background: projectDot(data.jobSiteId),
              flexShrink: 0,
            }}
          />
          {/* The project's own Brief — its stats, what has not moved, its
              walks and every record on it. Composed with `hrefWithParam` so
              `?project=` and `?workspace=` ride along untouched; a link that
              spelled its own query would answer for a different tenant than
              the page claims. */}
          <a
            href={hrefWithParam("page", "project-brief")}
            data-testid="project-brief-link"
            onClick={(event) => {
              event.preventDefault();
              onNavigate(hrefWithParam("page", "project-brief"));
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 32,
              padding: "0 13px",
              borderRadius: 10,
              background: C.surface,
              border: `1px solid ${C.line2}`,
              fontSize: 14.37,
              fontWeight: 600,
            }}
          >
            Project brief
          </a>
          {latestWalk && (
            <a
              href={hrefForWalk(latestWalk.id)}
              data-testid="project-latest-walk"
              onClick={(event) => {
                event.preventDefault();
                onOpenWalk(latestWalk.id);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                height: 32,
                padding: "0 13px",
                borderRadius: 10,
                background: C.surface,
                border: `1px solid ${C.line2}`,
                fontSize: 14.37,
                fontWeight: 600,
              }}
            >
              Latest walk
            </a>
          )}
          {stats.notReviewed > 0 ? (
            <a
              href={hrefForReview(data.jobSiteId)}
              data-testid="project-review-cta"
              onClick={(event) => {
                event.preventDefault();
                onOpenReview(data.jobSiteId);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                height: 32,
                padding: "0 13px",
                borderRadius: 10,
                background: C.gold,
                color: C.onGold,
                fontSize: 14.37,
                fontWeight: 700,
              }}
            >
              {`Review ${stats.notReviewed}`}
            </a>
          ) : (
            <span
              data-testid="project-nothing-to-review"
              style={{ ...META, fontSize: 10.35, color: C.green }}
            >
              Nothing to review
            </span>
          )}
        </div>
      </ViewHeader>

      <ViewBody>
        {refreshFailure && <StaleBanner failure={refreshFailure} since={data.generatedAt} />}

        {/* The band. Its FIRST cell loses the divider it would otherwise
            draw against the card's own edge — `:first-child` is a rule only
            CSS can express, so the band sets a negative inset instead and the
            first cell's border falls outside the rounded corner. */}
        <div
          className="statband"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            background: C.surface,
            border: `1px solid ${C.hair}`,
            borderRadius: 14,
            boxShadow: `0 1px 2px ${C.softShadow}`,
            padding: "18px 0",
            marginBottom: 20,
            overflow: "hidden",
          }}
        >
          <Stat
            testId="stat-open"
            label="Open items"
            value={String(stats.openItems)}
            sub="across all kinds"
            ink={C.ink}
          />
          <Stat
            testId="stat-stalled"
            label="Stalled"
            value={String(stats.stalled)}
            sub={`${stats.stallThresholdDays}+ days`}
            ink={stats.stalled > 0 ? C.red : C.ink}
          />
          <Stat
            testId="stat-not-reviewed"
            label="Not reviewed"
            value={String(stats.notReviewed)}
            sub="from walks"
            ink={stats.notReviewed > 0 ? C.amber : C.ink}
          />
          <Stat
            testId="stat-walks"
            label="Walks"
            value={String(data.walkTotal)}
            sub={stats.lastWalkAt ? `last ${dayLabel(stats.lastWalkAt)}` : "none yet"}
            ink={stats.lastWalkAt ? C.ink : C.amber}
          />
        </div>

        <div
          style={{
            display: "grid",
            // With no stalled records, the right column is the page's whole
            // answer. Do not leave an empty "Needs you first" card — or its
            // vacant column — to restate that nothing is needed.
            gridTemplateColumns:
              data.stalled.length > 0 ? "minmax(0, 1.6fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
            gap: 14,
            alignItems: "start",
          }}
        >
          {data.stalled.length > 0 && (
            <Card testId="project-stalled">
            <Eyebrow
              right={
                <span style={{ ...META, fontSize: 9.77, color: C.sub2 }}>
                  Stalled, longest first
                </span>
              }
            >
              Needs you first
            </Eyebrow>
            {data.stalled.map((row, index) => (
              <StalledRow
                key={row.id}
                row={row}
                siteName={data.siteName}
                href={hrefForItem(row.id)}
                onOpen={() => onOpenItem(row.id)}
                first={index === 0}
              />
            ))}
            </Card>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Card testId="project-walks">
              <Eyebrow
                right={
                  data.walkTotal > data.walks.length ? (
                    <span style={{ ...META, fontSize: 9.77, color: C.sub2 }}>
                      {`${data.walks.length} of ${data.walkTotal}`}
                    </span>
                  ) : undefined
                }
              >
                Recent walks
              </Eyebrow>
              {data.walks.length === 0 ? (
                <div style={{ fontSize: 14.37, color: C.sub, lineHeight: 1.5 }}>
                  This project has never been walked. Nothing here is stale — there is nothing
                  here yet.
                </div>
              ) : (
                data.walks.map((walk, index) => (
                  <WalkRow
                    key={walk.id}
                    walk={walk}
                    href={hrefForWalk(walk.id)}
                    onOpen={() => onOpenWalk(walk.id)}
                    first={index === 0}
                  />
                ))
              )}
            </Card>

            {data.recentEvidence.length > 0 && (
              <Card testId="project-evidence">
                <Eyebrow>Recent photos</Eyebrow>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {data.recentEvidence.slice(0, 6).map((evidence) => (
                    <a
                      key={evidence.itemId}
                      href={hrefForItem(evidence.itemId)}
                      onClick={(event) => {
                        event.preventDefault();
                        setEvidenceViewer(
                          data.recentEvidence.slice(0, 6).findIndex((candidate) => candidate.itemId === evidence.itemId),
                        );
                      }}
                      style={{ width: 64 }}
                      title={evidence.title}
                    >
                      <EvidenceTile
                        jobSiteId={evidence.jobSiteId}
                        url={evidence.thumbnailUrl}
                        alt=""
                        height={52}
                      />
                    </a>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>

        {stats.openItems === 0 && data.walkTotal === 0 && (
          <div style={{ marginTop: 16 }}>
            <EmptyPanel
              title="Nothing has happened on this project yet"
              body="No walk has been recorded and no record has been generated. This page fills in as soon as somebody walks the site."
            />
          </div>
        )}
      </ViewBody>
      {evidenceViewer !== null && recentPhotos[evidenceViewer] && (
        <PhotoFullscreenViewer
          photos={recentPhotos}
          index={evidenceViewer}
          onStep={setEvidenceViewer}
          onClose={() => setEvidenceViewer(null)}
        />
      )}
    </>
  );
}
